import React, { useRef, useState } from 'react';
import useTranslate from '../../../../../customHooks/useTranslate';
import Card from '../../../../../muiComponents/Card';
import Icon from '../../../../../muiComponents/Icon';
import FaviconPreview from './favicon/FaviconPreview';
import BrowserTab from './favicon/BrowserTab';
import { useZitoneoSettings } from '../../../contexts/ZitoneoContentContext';
import DeleteImagePopUp from '../settings/DeleteImagePopUp';
import ImageCropPopUp from '../settings/logo/ImageCropPopUp';
import ImageUploadActions from './ImageUploadActions';
import { getImageDimensions } from '../../../utils/imageUtils';
import { MIN_ICON_WIDTH_AND_HEIGHT } from '../../../utils/zitoneoServicesUtils';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import { EMPTY_FILE_STATE } from '../../../utils/imageUtils';

const FaviconSection = () => {
    const translate = useTranslate();
    const { HotelName, IconFile, uploadIconImageToDDBB, saveHotelContent } =
        useZitoneoSettings();
    const { setErrorMessage } = useNotification();
    const faviconInputRef = useRef<HTMLInputElement>(null);
    const [selectedIconFile, setSelectedIconFile] = useState<File | null>(null);
    const [isCropPopUpOpen, setIsCropPopUpOpen] = useState(false);
    const [isDeletePopUpOpen, setIsDeletePopUpOpen] = useState(false);

    const handleIconSelection = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];

        if (!file) {
            event.target.value = '';
            return;
        }

        try {
            const { width, height } = await getImageDimensions(file);

            if (
                width < MIN_ICON_WIDTH_AND_HEIGHT ||
                height < MIN_ICON_WIDTH_AND_HEIGHT
            ) {
                setErrorMessage(
                    `${translate(
                        'error_min_icon_dimensions'
                    )} ${MIN_ICON_WIDTH_AND_HEIGHT}x${MIN_ICON_WIDTH_AND_HEIGHT}px.`
                );
                event.target.value = '';
                return;
            }
        } catch (error) {
            setErrorMessage(translate('error_invalid_image'));
            event.target.value = '';
            return;
        }

        setSelectedIconFile(file);
        setIsCropPopUpOpen(true);
        event.target.value = '';
    };

    const handleSaveIcon = async (file: File) => {
        await uploadIconImageToDDBB(file);
        setSelectedIconFile(null);
        setIsCropPopUpOpen(false);
    };

    const handleConfirmDeleteIcon = async () => {
        await saveHotelContent({
            IdIconFile: 0,
            IconFile: EMPTY_FILE_STATE,
        });
        setIsDeletePopUpOpen(false);
    };

    return (
        <div className='hotel-content-settings__section'>
            <div className='settings-section__header'>
                <h5 className='settings-section__title'>
                    {translate('favicon')}
                </h5>
                <p className='settings-section__description'>
                    {translate('favicon_description')}
                </p>
            </div>
            <Card className='padding20 favicon-card flex-column gap-20'>
                <BrowserTab
                    title={HotelName}
                    src={IconFile?.VirtualPath || ''}
                />
                <div className='flex gap-20'>
                    <div className='zitoneo-icon-placeholder'>
                        <FaviconPreview src={IconFile?.VirtualPath || ''} />
                    </div>
                    <div className='flex-column gap-20'>
                        <div className='flex-column gap-5'>
                            <p className='settings__label'>
                                {translate(
                                    IconFile?.VirtualPath
                                        ? 'icon_uploaded'
                                        : 'no_icon_uploaded'
                                )}
                            </p>
                            <p>{translate('favicon_file_description')}</p>
                        </div>
                        <ImageUploadActions
                            inputId='icon-upload'
                            inputRef={faviconInputRef}
                            onChange={handleIconSelection}
                            onRemove={() => setIsDeletePopUpOpen(true)}
                            uploadLabel={translate('upload_icon')}
                            showRemove={Boolean(IconFile?.VirtualPath)}
                        />
                    </div>
                </div>
                <ul className='zitoneo-favicon__recomendation-list'>
                    <li className='zitoneo-favicon__recomendation-item'>
                        <Icon iconName='CropSquare' />
                        <p>{translate('favicon_recommendation_1')}</p>
                    </li>
                    <li className='zitoneo-favicon__recomendation-item'>
                        <Icon iconName='Image' />
                        <p>{translate('favicon_recommendation_2')}</p>
                    </li>
                    <li className='zitoneo-favicon__recomendation-item'>
                        <Icon iconName='ZoomIn' />
                        <p>{translate('favicon_recommendation_3')}</p>
                    </li>
                </ul>
            </Card>
            <DeleteImagePopUp
                open={isDeletePopUpOpen}
                image={{
                    name: IconFile?.Caption || translate('favicon'),
                    label: translate('favicon'),
                    src: IconFile?.VirtualPath,
                    alt: translate('favicon'),
                }}
                onDelete={handleConfirmDeleteIcon}
                onCancel={() => setIsDeletePopUpOpen(false)}
            />
            <ImageCropPopUp
                isOpen={isCropPopUpOpen}
                file={selectedIconFile}
                onClose={() => setIsCropPopUpOpen(false)}
                onSave={handleSaveIcon}
                minWidth={MIN_ICON_WIDTH_AND_HEIGHT}
                minHeight={MIN_ICON_WIDTH_AND_HEIGHT}
                aspectRatio={1}
                titleText='icon_crop'
            />
        </div>
    );
};

export default FaviconSection;
